import React, { useState, useMemo } from 'react';
import {
    Box, Typography, Chip, Button, Avatar, Popover, useTheme,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Badge from '@mui/material/Badge';
import { styled } from '@mui/material/styles';
import MoreVertIcon from '../../../components/MoreVertIcon';
import DeleteDialog from '../../../components/DeleteDialog';
import { Link } from 'react-router-dom';
import { tokens } from '../../../theme';
import v2Api from '../../../utils/v2Api';
import UpdateModalV2 from './UpdateModalV2';
import getStudentName from '../../../utils/getStudentName';
import { latestSessionByStudent, isSessionDone, isExpiredForStudent } from './v2/resultUtils';
import dayjs from 'dayjs';

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: 10, top: 3,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 2px',
    },
}));

var options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: '2-digit',
    minute: '2-digit'
};

var options_without_time = {
    year: "numeric",
    month: "short",
    day: "numeric"
};

export default function TestBoxV2({
    courseTest, courseId, students, studentsStartEndDateMap, userRole, userId, studentId,
    setBackdropOpen, setSuccessAlertOpen, setErrorAlertOpen,
    setSuccessAlertMessage, setErrorAlertMessage,
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Popover (same pattern as v1 TestBox)
    const [anchorEl, setAnchorEl] = useState(null);
    const handleMoreVertIconClick = (event) => {
        if (userRole === "student") return;
        setAnchorEl(event.currentTarget);
    };
    const handlePopOverClose = () => setAnchorEl(null);
    const popoverOpen = Boolean(anchorEl);
    const popoverId = popoverOpen ? 'popover-v2' : undefined;

    const [updateOpen, setUpdateOpen] = useState(false);
    const [reopenDialog, setReopenDialog] = useState({ open: false, sessionId: null, studentName: '' });

    const handleReopenSession = async () => {
        try {
            await v2Api.post(`/sessions/${reopenDialog.sessionId}/reopen`);
            setReopenDialog({ open: false, sessionId: null, studentName: '' });
            setSuccessAlertMessage('Session reopened — student can retake the test.');
            setSuccessAlertOpen(true);
            // Refresh by reloading the page
            window.location.reload();
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.message || 'Failed to reopen session');
            setErrorAlertOpen(true);
        }
    };

    const test = courseTest.v2_test;
    const sessions = courseTest.v2_student_test_sessions || [];
    const isAdmin = userRole === 'admin' || userRole === 'teacher';

    // Retakeable tests have multiple attempts per student — collapse to the latest
    // attempt per student (shared helper, deterministic tie-break), used both for
    // the student's own status and the admin chips.
    const latestByStudent = useMemo(() => latestSessionByStudent(sessions), [sessions]);
    const latestSessions = [...latestByStudent.values()];
    const mySession = latestByStudent.get(studentId);
    const status = mySession?.status || 'not_started';

    // Student visibility: if specific students assigned and I'm not in the list
    if (userRole === 'student' && studentId) {
        const assignedStudents = courseTest.v2_course_test_students || [];
        if (assignedStudents.length > 0 && !assignedStudents.some(s => s.student_id === studentId)) {
            return null;
        }
    }

    const handleDeleteTest = () => {
        setAnchorEl(null);
        setBackdropOpen(true);
        v2Api.delete(`/course-tests/${courseTest.id}`, { force: true })
            .then(() => {
                setBackdropOpen(false);
                setSuccessAlertMessage("Test deleted successfully!");
                setSuccessAlertOpen(true);
                window.location.reload();
            })
            .catch((err) => {
                setBackdropOpen(false);
                setErrorAlertMessage(err.response?.data?.message || "Something went wrong");
                setErrorAlertOpen(true);
            });
    };

    // Due date render (same as v1)
    const dueDateRender = () => {
        if (!courseTest.due_date) return <Box />;
        return (
            <Typography variant="h7" color={new Date(courseTest.due_date) < new Date() ? "error" : "textPrimary"} sx={{ my: "auto", ml: "auto" }}>
                <span style={{ fontWeight: "bold" }}>Due</span> {new Date(courseTest.due_date).toLocaleDateString("en", options)}
            </Typography>
        );
    };

    // Assigned students render (same pattern as v1)
    const assignedStudentsRender = () => {
        if (!students || students.length === 0) return <Box />;

        // Build status maps from v2 sessions
        const submitted_students = latestSessions
            .filter(s => s.status === 'submitted' || s.status === 'graded')
            .map(s => s.student_id);
        const in_progress_students = latestSessions
            .filter(s => s.status === 'in_progress')
            .map(s => s.student_id);

        // Determine which students are assigned
        const assignedStudentIds = (courseTest.v2_course_test_students || []).map(s => s.student_id);
        const allMode = assignedStudentIds.length === 0;

        const relevantStudents = students.filter(s => {
            if (!allMode && !assignedStudentIds.includes(s.id)) return false;
            const dates = studentsStartEndDateMap?.[s.id];
            if (!dates) return true;
            return !dayjs().isAfter(dayjs(dates.end_date)) && !dayjs().isBefore(dayjs(dates.start_date));
        });

        const not_started_students = relevantStudents
            .map(s => s.id)
            .filter(id => !submitted_students.includes(id) && !in_progress_students.includes(id));

        return (
            <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", px: "1rem", py: "0.5rem" }}>
                {relevantStudents.map((student) => {
                    const student_name = getStudentName(student);
                    if (submitted_students.includes(student.id)) {
                        const sess = latestByStudent.get(student.id);
                        const gradingDone = sess && (sess.status === 'graded' || sess.status === 'submitted');
                        const hasPendingFrq = (sess?.v2_student_part_scores || []).some(
                            ps => ps.grading_status === 'pending' || ps.grading_status === 'grading'
                        );
                        const isFullyGraded = gradingDone && !hasPendingFrq;
                        const isOpened = sess?.is_result_opened;

                        const chipClick = isAdmin ? () => setReopenDialog({ open: true, sessionId: sess?.id, studentName: student_name }) : undefined;
                        const chipSx = { mr: "0.3rem", mb: "0.3rem", ...(isAdmin ? { cursor: 'pointer' } : {}) };

                        if (isFullyGraded) {
                            return (
                                <StyledBadge key={student.id} color="primary" variant={isOpened ? 'standard' : 'dot'}>
                                    {isOpened && <CheckCircleIcon sx={{ position: "absolute", top: "-0.5rem", right: "0", backgroundColor: colors.primary[600], borderRadius: '50%', zIndex: 1 }} />}
                                    <Chip color="secondary" label={student_name} sx={chipSx} onClick={chipClick} />
                                </StyledBadge>
                            );
                        }
                        return <Chip key={student.id} color="secondary" label={student_name} sx={chipSx} onClick={chipClick} />;
                    } else if (in_progress_students.includes(student.id)) {
                        return <Chip key={student.id} color="warning" label={student_name} sx={{ mr: "0.3rem", mb: "0.3rem" }} />;
                    } else if (not_started_students.includes(student.id)) {
                        return <Chip key={student.id} label={student_name} sx={{ mr: "0.3rem", mb: "0.3rem" }} />;
                    }
                    return null;
                })}
            </Box>
        );
    };

    // Button color logic (match v1)
    const getButtonProps = () => {
        if (isAdmin) {
            return { label: "View the Test", color: colors.blueAccent[600], to: `/classrooms/${courseId}/test-v2/${courseTest.id}/grading` };
        }
        if (status === 'submitted' || status === 'graded') {
            return { label: "See the Result", color: "purple", to: `/classrooms/${courseId}/test-v2/${courseTest.id}/result/${mySession?.id}` };
        }
        if (status === 'in_progress') {
            return { label: "Resume", color: colors.blueAccent[600], to: `/classrooms/${courseId}/test-v2/${courseTest.id}` };
        }
        return { label: "Take the Test", color: "#51BE90", to: `/classrooms/${courseId}/test-v2/${courseTest.id}` };
    };

    const btnProps = getButtonProps();
    // Retakeable vocab tests: once finished, offer a fresh attempt (new random subset).
    const showRetake = !isAdmin && test?.allow_retake && isSessionDone(mySession);
    const retakeTo = `/classrooms/${courseId}/test-v2/${courseTest.id}?retake=true`;
    // Past the due date, a vocab test is no longer visitable by students — hide the
    // action buttons and show a message instead (admins/non-vocab unaffected).
    const expired = isExpiredForStudent(courseTest, test, isAdmin);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: colors.primary[600],
                borderRadius: "5px",
                border: "0.1px solid #a3a3a3",
                mb: "1.8rem",
            }}
        >
            {/* Header row: avatar + title + due date + more icon */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    borderBottom: "0.1px solid #a3a3a3",
                    py: "0.5rem",
                    px: "1rem",
                    alignItems: "center",
                }}
            >
                <Avatar sx={{ width: 30, height: 30 }}>
                    <AssignmentIcon />
                </Avatar>
                <Typography variant="h5" sx={{ my: "auto", ml: "1rem" }}>
                    {courseTest.title || test?.title}
                </Typography>
                {dueDateRender()}
                {userRole !== "student" &&
                    <>
                        <MoreVertIcon handleClick={handleMoreVertIconClick} />
                        <Popover
                            id={popoverId}
                            open={popoverOpen}
                            anchorEl={anchorEl}
                            onClose={handlePopOverClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        >
                            <Button onClick={() => { setUpdateOpen(true); setAnchorEl(null); }} sx={{ p: 1, width: "100%" }}>Update</Button>
                            <DeleteDialog
                                handleDelete={handleDeleteTest}
                                setAnchorEl={setAnchorEl}
                                dialogTitle={"Are you sure you want to delete this test?"}
                            />
                        </Popover>
                        <UpdateModalV2
                            open={updateOpen}
                            onClose={() => setUpdateOpen(false)}
                            courseTest={courseTest}
                            students={students}
                            studentsStartEndDateMap={studentsStartEndDateMap}
                            userRole={userRole}
                            setBackdropOpen={setBackdropOpen}
                            setSuccessAlertOpen={setSuccessAlertOpen}
                            setErrorAlertOpen={setErrorAlertOpen}
                            setSuccessAlertMessage={setSuccessAlertMessage}
                            setErrorAlertMessage={setErrorAlertMessage}
                        />
                    </>
                }
            </Box>

            {/* Content: posted date + instructions + action button */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    borderBottom: "0.1px solid #a3a3a3",
                    py: "1rem",
                    px: "0.3rem",
                }}
            >
                <Typography variant="h7" color={colors.grey[700]} sx={{ my: "auto", ml: "1rem" }}>
                    Posted on: {new Date(courseTest.createdAt).toLocaleDateString("en", options_without_time)}
                </Typography>
                <Typography variant='h7' sx={{ mt: "1rem", ml: "1rem" }}>
                    {courseTest.content && courseTest.content.split("\n").map((line, index) => (
                        <span key={index}>{line}<br /></span>
                    ))}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        mt: "1rem",
                        mx: "1rem",
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column" }} />
                    <Box mt="auto" display="flex" flexDirection="row" gap="0.5rem">
                        {expired ? (
                            <Typography variant="h7" color="error" sx={{ my: "auto" }}>
                                Results are no longer available
                            </Typography>
                        ) : (
                            <>
                                {showRetake && (
                                    <Link to={retakeTo} style={{ color: "white", textDecoration: "none" }}>
                                        <Button
                                            type="button"
                                            variant="contained"
                                            style={{ backgroundColor: "#51BE90", width: "11rem", fontSize: "9px" }}
                                        >
                                            {test?.questions_per_attempt ? `Retake (new ${test.questions_per_attempt})` : 'Retake'}
                                        </Button>
                                    </Link>
                                )}
                                {isAdmin && (
                                    <Link to={`/classrooms/${courseId}/test-v2/${courseTest.id}/review`} style={{ color: "white", textDecoration: "none" }}>
                                        <Button
                                            type="button"
                                            variant="contained"
                                            title="Go over the questions and answer keys — no student data"
                                            style={{ backgroundColor: "#8657c4", width: "11rem", fontSize: "9px" }}
                                        >
                                            Review
                                        </Button>
                                    </Link>
                                )}
                                <Link to={btnProps.to} style={{ color: "white", textDecoration: "none" }}>
                                    <Button
                                        type="button"
                                        variant="contained"
                                        style={{
                                            backgroundColor: btnProps.color,
                                            width: "11rem",
                                            fontSize: "9px",
                                        }}
                                    >
                                        {btnProps.label}
                                    </Button>
                                </Link>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Status legend */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    width: "100%",
                    py: "0.5rem",
                    px: "1rem",
                }}
            >
                <CircleIcon fontSize='10px' color='secondary' />
                <Typography variant='h9' sx={{ ml: "0.3rem" }}>Complete Test</Typography>
                <CircleIcon fontSize='10px' color='warning' sx={{ ml: "1rem" }} />
                <Typography variant='h9' sx={{ ml: "0.3rem" }}>During Test</Typography>
                <CircleIcon fontSize='10px' sx={{ ml: "1rem", color: colors.grey[950] }} />
                <Typography variant='h9' sx={{ ml: "0.3rem" }}>Not Start Yet</Typography>
            </Box>

            {/* Student status chips */}
            {assignedStudentsRender()}

            {/* Reopen session dialog */}
            <Dialog open={reopenDialog.open} onClose={() => setReopenDialog({ open: false, sessionId: null, studentName: '' })} maxWidth="xs" fullWidth>
                <DialogTitle>Reopen Test Session</DialogTitle>
                <DialogContent>
                    <Typography>
                        Allow <strong>{reopenDialog.studentName}</strong> to retake this test? Their previous answers will be preserved.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReopenDialog({ open: false, sessionId: null, studentName: '' })} color="inherit">Cancel</Button>
                    <Button onClick={handleReopenSession} variant="contained">Reopen</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
